import { cn } from '@atlas/ui-primitives'
import type { ComponentPropsWithoutRef, Ref } from 'react'

/**
 * Props for the {@link CardShell} component.
 *
 * @property as - Rendered element: 'div' (default) | 'section' | 'aside' | 'article' | 'li' | 'form'
 * @property className - Optional additional Tailwind classes, merged over the surface
 * @property children - Card content
 *
 * @remarks
 * `li` and `form` are here because card-shaped list items and card-shaped forms
 * are surfaces like any other, and rendering them as a `div` would either break
 * the list's semantics or strip the form's submit behaviour. The union stays
 * closed to non-interactive containers: a card-shaped `button` or link also needs
 * focus, hover and press treatment, which is a component of its own rather than a
 * surface with an `as`.
 */
export interface CardShellProps extends ComponentPropsWithoutRef<'div'> {
	as?: 'div' | 'section' | 'aside' | 'article' | 'li' | 'form'
	/** Widened past `HTMLDivElement` because `as` chooses the element this lands on. */
	ref?: Ref<HTMLElement>
}

/**
 * The bare content-card surface: the token radius, border, elevated background,
 * and padding every card shares — with no heading row. Use {@link SectionCard}
 * for titled sections; use this for card-shaped containers that have none.
 */
export function CardShell({ as: Element = 'div', className, ref, ...props }: CardShellProps) {
	// `as` picks the tag at runtime, so TypeScript cannot narrow the intrinsic
	// element's prop and ref types from the union. Both casts land on the same
	// element the caller chose; the surface props are identical across it.
	const Tag = Element as 'div'
	return (
		<Tag
			{...props}
			ref={ref as Ref<HTMLDivElement>}
			className={cn('rounded-ds-card border border-ds-border bg-ds-surface-elevated p-5', className)}
		/>
	)
}
